/**
 * ReassignOrderConfirm — the reassignment → order safety-valve dialog.
 *
 * Shown after a customer override (email-only, the safe default) when the
 * email's linked order(s) diverge from the new customer. It lets the operator
 * opt in to ALSO correct the customer on the editable (pre-Sage) order(s), and
 * warns — without offering an action — about any order already in Sage (which
 * needs a Sage-side correction). Choosing "Just the email" leaves the orders
 * untouched. On confirm it re-issues the override with `updateLinkedOrders`.
 */

import { useState } from 'react';
import { portalService } from '../../services/portal';
import type { LinkedOrderState } from '../../types/portal';
import { ORDER_STATUS_LABELS, type OrderStatus } from '../../types/orders';
import { extractApiErrorMessage } from '../../utils/emailActionUtils';
import styles from './ReassignOrderConfirm.module.css';

interface ReassignOrderConfirmProps {
  emailId: string;
  /** The customer the email was just reassigned to (the target). */
  customerNo: string;
  customerName: string | null;
  /** Linked orders from the email-only override response. */
  linkedOrders: LinkedOrderState[];
  /** Called once the operator resolves the dialog (either choice). */
  onResolved: () => void;
  onToast?: (message: string, kind: 'success' | 'error') => void;
}

const statusLabel = (status: string): string =>
  ORDER_STATUS_LABELS[status as OrderStatus] ?? status;

const orderLabel = (o: LinkedOrderState): string =>
  o.po_number ? `PO ${o.po_number}` : o.sage_order_no || 'Order';

const ReassignOrderConfirm: React.FC<ReassignOrderConfirmProps> = ({
  emailId,
  customerNo,
  customerName,
  linkedOrders,
  onResolved,
  onToast,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only diverging orders are actionable/notable. Editable → can be corrected;
  // Sage-locked → warn only. (Cancelled/terminal orders are ignored.)
  const editable = linkedOrders.filter((o) => o.editable && o.customer_diverges);
  const locked = linkedOrders.filter((o) => o.sage_locked && o.customer_diverges);

  const target = customerName || customerNo;

  const handleUpdateOrders = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await portalService.overrideCustomer(emailId, customerNo, {
        updateLinkedOrders: true,
      });
      portalService.invalidateStatsCache();
      onToast?.(
        editable.length === 1
          ? 'Order customer updated.'
          : `${editable.length} order customers updated.`,
        'success',
      );
      onResolved();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update the order.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-label="Update linked order">
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            {editable.length > 0 ? 'Update the linked order too?' : 'Linked order in Sage'}
          </span>
        </div>

        <div className={styles.body}>
          {editable.length > 0 && (
            <>
              <p className={styles.lead}>
                This email is linked to {editable.length === 1 ? 'an order' : 'orders'}{' '}
                with a different customer. Update {editable.length === 1 ? 'it' : 'them'} to{' '}
                <strong>{target}</strong> too, or keep the correction on the email only?
              </p>
              <ul className={styles.orderList}>
                {editable.map((o) => (
                  <li key={o.order_id} className={styles.orderRow} data-testid="editable-order">
                    <span className={styles.orderName}>{orderLabel(o)}</span>
                    <span className={styles.statusBadge}>{statusLabel(o.status)}</span>
                    <span className={styles.change}>
                      {o.customer_no || '—'} <span className={styles.arrow}>→</span>{' '}
                      <strong>{customerNo}</strong>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {locked.length > 0 && (
            <div className={styles.warning} data-testid="sage-locked-warning">
              <span className={styles.warnIcon} aria-hidden="true">⚠</span>
              <div>
                <strong>
                  {locked.length === 1 ? 'This order is' : 'These orders are'} already in Sage
                </strong>{' '}
                and {locked.length === 1 ? "won't" : "won't"} be changed here — correct{' '}
                {locked.length === 1 ? 'it' : 'them'} on the Sage side:
                <ul className={styles.lockedList}>
                  {locked.map((o) => (
                    <li key={o.order_id} data-testid="locked-order">
                      {orderLabel(o)} — {statusLabel(o.status)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          {editable.length > 0 ? (
            <>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={onResolved}
                disabled={submitting}
              >
                Just the email
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleUpdateOrders}
                disabled={submitting}
              >
                {submitting
                  ? 'Updating…'
                  : editable.length === 1
                    ? 'Update order'
                    : `Update ${editable.length} orders`}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={onResolved}
              disabled={submitting}
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReassignOrderConfirm;
